import {
  FlexBox,
  FormField,
  FormGrid,
  Modal,
  ModalBody,
  ModalFoot,
  ModalHead,
  SelectionList,
  Spinner,
  Text,
} from '@dixa/lib-shared-habla';
import { isResponseWithData } from 'lib-shared-auth';
import noop from 'lodash/noop';
import moment from 'moment';
import { useEffect, useMemo } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';

import { mimChatbotsApi } from 'apis/chatbots';
import {
  KnowledgeBase,
  knowledgeBasesApi,
  KnowledgeBaseType,
} from 'apis/knoledgeBases';
import { HookFormSelectionList } from 'components/hookForm';
import { NotificationType, useNotifications } from 'hooks/useNotifications';
import { useFeatureFlag } from 'utils/featureFlagging/useFeatureFlag';
import { createAppModal } from 'utils/modal';
import { useAppNavigate } from 'utils/url/useAppNavigate';

import styles from './chatbotUpdateSourcesModal.module.scss';

const formId = 'mim-chatbot-update-sources';

type FieldValues = {
  knowledgeBases: KnowledgeBase[];
};

export const ChatbotUpdateSourcesModal = createAppModal(
  { type: 'settings:chatbotUpdateSourcesModal' },
  ({
    isOpen,
    hide,
    modal: {
      arg: { organizationId, id },
    },
  }): JSX.Element => {
    const [updateChatbot, { isLoading: isUpdating }] =
      mimChatbotsApi.useUpdateMimChatbotSourcesMutation();

    const flags = useFeatureFlag(['knowledge-access', 'website-data-source']);
    const knowledgeSource: KnowledgeBaseType[] = useMemo(
      () =>
        [
          'elevio',
          ...(flags.knowledgeAccess ? ['dixa_knowledge'] : []),
          ...(flags.websiteDataSource ? ['website'] : []),
        ] as KnowledgeBaseType[],
      [flags.knowledgeAccess, flags.websiteDataSource],
    );

    const {
      chatbot,
      isLoading: isChatbotLoading,
      isFetching: isChatbotFetching,
    } = mimChatbotsApi.useReadMimChatbotByIdQuery(
      { id },
      {
        selectFromResult: ({ data, ...rest }) => {
          return {
            ...rest,
            chatbot: data?.data,
          };
        },
      },
    );

    const {
      knowledgeBases,
      isLoading: isDataSourceLoading,
      isFetching: isDataSourceFetching,
    } = knowledgeBasesApi.useReadKnowledgeBasesQuery(
      { organizationId: organizationId, source: knowledgeSource },
      {
        skip: !chatbot,
        selectFromResult: ({ data, ...rest }) => ({
          ...rest,
          knowledgeBases:
            data && Array.isArray(data.data.knowledgeBases)
              ? [...data.data.knowledgeBases].reverse()
              : [],
        }),
      },
    );

    const isLoading = isChatbotLoading || isDataSourceLoading;
    const isFetching = isChatbotFetching || isDataSourceFetching;

    const navigate = useAppNavigate();
    const notify = useNotifications();

    const form = useForm<FieldValues>({
      defaultValues: {
        knowledgeBases: [],
      },
      mode: 'all',
    });
    useEffect(() => {
      form.reset({
        knowledgeBases: chatbot?.knowledgeBases ?? [],
      });
    }, [chatbot]);

    const onSubmit: SubmitHandler<FieldValues> = async ({ knowledgeBases }) => {
      try {
        const response = await updateChatbot({
          id,
          name: chatbot!.name,
          knowledgeBases: [
            ...knowledgeBases.map((kb) => ({
              id: kb.id,
              includeInternalArticles: false,
            })),
          ],
        }).unwrap();

        if (isResponseWithData(response)) {
          notify(
            {
              title: 'Success',
              text: `"${response.data?.name || 'Chatbot'}" has been updated.`,
              icon: 'i-done',
              time: 5000,
            },
            NotificationType.Success,
          );
          hide?.();
          navigate({ path: '/settings/chatbots/mim' });
        }
      } catch (error) {
        notify(
          {
            title: 'Error while updating chatbot.',
            text: 'Something went wrong.',
            icon: 'exclamation',
            className: 'danger',
            time: 5000,
          },
          NotificationType.Error,
        );
      }
    };

    const findKbById = (
      id: string,
      items: KnowledgeBase[],
    ): KnowledgeBase | undefined => {
      return items.find((i) => i.id === id);
    };

    const isSyncing = (kb: KnowledgeBase) => kb.state === 'syncing';

    const getFilteredKnowledgeBases = (
      knowledgeBases: KnowledgeBase[],
      type: KnowledgeBaseType,
      title: string,
    ) => {
      const filtered = knowledgeBases.filter((item) => item.source === type);

      if (filtered.length === 0) return null;

      return (
        <SelectionList.Section key={type} title={title}>
          {filtered.map((kb) => (
            <SelectionList.Item key={kb.id} value={kb}>
              <Text.Paragraph>{kb.name}</Text.Paragraph>
              <Text.Paragraph subdued variant="4">
                {moment(kb.created_at).format('DD/MM/YYYY HH:mm:ss')}{' '}
                {isSyncing(kb) && <span>- Knowledge base sync in process</span>}
              </Text.Paragraph>
            </SelectionList.Item>
          ))}
        </SelectionList.Section>
      );
    };

    return (
      <Modal isOpen={isOpen} onClose={hide} className={styles.modal}>
        <ModalHead
          title="Connect existing data source"
          onClose={hide}
        ></ModalHead>
        <ModalBody>
          {isLoading || isFetching ? (
            <FlexBox flex="1" justifyContent="center" alignItems="center">
              <Spinner />
            </FlexBox>
          ) : (
            <form
              id={formId}
              noValidate
              tabIndex={-1}
              onSubmit={form.handleSubmit(onSubmit)}
            >
              <Text.Paragraph variant="2" wrap="nowrap" withMarginBottom>
                Choose some of the existing data sources from the list below.
              </Text.Paragraph>
              <FormGrid>
                <FormField>
                  <HookFormSelectionList
                    control={form.control}
                    name="knowledgeBases"
                    label="Data source"
                    rules={{ required: 'Data source is required' }}
                    componentProps={{
                      multiple: true,
                      equalityFn: (item1, item2) => item1.id === item2.id,
                      getItemTitleByValue: (
                        kb: KnowledgeBase | null | undefined,
                      ) => {
                        const selectedChatbot = findKbById(
                          kb!.id,
                          knowledgeBases,
                        );
                        return (
                          <Text.Paragraph>
                            {selectedChatbot?.name ?? ''}
                          </Text.Paragraph>
                        );
                      },
                    }}
                  >
                    {getFilteredKnowledgeBases(
                      knowledgeBases,
                      'elevio',
                      'Elevio',
                    )}
                    {getFilteredKnowledgeBases(
                      knowledgeBases,
                      'dixa_knowledge',
                      'Dixa Knowledge',
                    )}
                    {getFilteredKnowledgeBases(
                      knowledgeBases,
                      'website',
                      'Website',
                    )}
                  </HookFormSelectionList>
                </FormField>
              </FormGrid>
            </form>
          )}
        </ModalBody>

        <ModalFoot
          cancelName="Cancel"
          cancelOnClick={hide}
          confirmName="Connect data source"
          confirmOnClick={noop}
          confirmProps={{
            isLoading: isUpdating,
            loadingIndicator: 'Updating...',
            type: 'submit',
            form: formId,
            disabled:
              isLoading ||
              !form.formState.isValid ||
              form.formState.isSubmitting ||
              !form.formState.isDirty,
          }}
        />
      </Modal>
    );
  },
);
